import { ErrorHandler, inject } from "@angular/core";
import { FgbError } from "./fgb-error";
import { ErrorStateService } from "./error.service";
import { TranslateService } from "@ngx-translate/core";
import { Router } from "@angular/router";
import { LoggingService } from "../core/logging.service";
import { HttpErrorResponse } from "@angular/common/http";
// import { TranslateService } from "@ngx-translate/core";

/**
 * Global error handler for the application
 * 
 * component level error handling example
 * 
 * Usage:
 * 
 * import ErrorComponent 
 * 
 * Add the following to the html
 * 
 * <fgbcl-error></fgbcl-error>
 * 
 * Error handling in the component and service example
 * 
 * @example 
 *  someMethod(): void{
 *    const test = 'string';
 *    
 *    if( typeof test === 'string' ){
 *     throw new FgbError({ message: 'this is a string!', translateKey: 'error.code.string' });
 *    }
 *  }
 * 
 * service level error handling example
 * @example
 *  someMethod(): Observable<any>{
 *   return this.http.get('url').pipe(
 *      catchError((err: any) => throwError(() => new FgbError(err)))
 *   );
 * }
 */
export class FgbErrorHandler implements ErrorHandler{
  private translate: TranslateService = inject(TranslateService);
  private errorService: ErrorStateService = inject(ErrorStateService);
  private router: Router = inject(Router);
  private logService: LoggingService = inject(LoggingService);

  handleError(error: any): void {

    if( error instanceof FgbError){
        // If translateKey is provided, the message will be translated, otherwise the message will be displayed
        // If from HttpErrorResponse, the translateKey will be translated first, while the message serves as a fallback or a reason for the error 
        const message = error.translateKey ? this.translate.instant(error.translateKey) : error.message;
        this.errorService.setError(message).clearAfter(error.clearAfter);

        // By default we will not show the error in the console
        if( !error.showInConsole ){
            return;
        }
    }

    /**
     * If the error is an HttpErrorResponse and the status is 403, redirect the user to first time.
     * No need to call member status as 403 is reserved for first time users.
     * Expected behaviour should be that the guard runs first then if user is already logged in and gets
     * a 403, the error handler will redirect the user to first time registration.
     */
    if (error instanceof HttpErrorResponse && error.status === 403) {

      this.logService.info('403 Forbidden response received, user is first time user.');

      this.setRedirectUrlAfterRegistration(this.router.url);

      this.logService.info('Redirecting to first time user registration...');
      this.router.navigate(['/register/firsttime']);
    }
    
    console.error('ERROR', error);
  }

  private setRedirectUrlAfterRegistration(url: string): void {
    const queryStringParameter: string = 'returnUrl=';
    let startIndex: number = url.toUpperCase().indexOf(queryStringParameter.toUpperCase());

    if (!isNaN(startIndex)) {
      startIndex += queryStringParameter.length;
      let endIndex = url.indexOf('&');
      let substrLength: number | undefined =  endIndex ? endIndex - startIndex : undefined;

      url = url.substr(startIndex, substrLength);
      url = decodeURIComponent(url);
    }

    this.logService.debug('Setting redirectToUrlAfterRegistration session key to:', url);
    sessionStorage.setItem('redirectToUrlAfterRegistration', url);
  }

}